// FIN-867 (in-app-feedback stack PR4): pure logic shared by FeedbackDialog (packages/ui) and,
// per the plan, reusable later by src/'s consultant FeedbackPanel (FIN-550d). Nothing here
// touches React or the network — see FeedbackDialog.tsx / FeedbackContextProvider.tsx for those.

export const FEEDBACK_MAX_MESSAGE_LENGTH = 5000
/** The counter only appears once a message is within 500 characters of the limit. */
export const FEEDBACK_COUNTER_THRESHOLD = FEEDBACK_MAX_MESSAGE_LENGTH - 500

function trimmedLength(message: string): number {
  return message.trim().length
}

/** EC1: empty/whitespace-only is invalid. EC2: over 5000 (trimmed) is invalid. */
export function isFeedbackMessageValid(message: string): boolean {
  const length = trimmedLength(message)
  return length > 0 && length <= FEEDBACK_MAX_MESSAGE_LENGTH
}

export function isFeedbackMessageTooLong(message: string): boolean {
  return trimmedLength(message) > FEEDBACK_MAX_MESSAGE_LENGTH
}

export function shouldShowFeedbackCounter(message: string): boolean {
  return message.length >= FEEDBACK_COUNTER_THRESHOLD
}

const MAX_CONTEXT_KEYS = 50
const MAX_CONTEXT_STRING_LENGTH = 500

/**
 * EC10: a safe merge of page-contributed context. Never throws — a throwing getter, a huge
 * value, a circular reference, or a non-serializable value (bigint, symbol) is dropped rather
 * than blocking the rest of the context or the submission itself. Caps key count and
 * per-string length; feedbackService.ts (the server) re-applies the identical caps plus a
 * total-byte-size cap as the authoritative boundary — this is the client-side UX layer only.
 */
export function sanitizeFeedbackContext(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {}

  const out: Record<string, string> = {}
  let count = 0
  for (const [key, rawValue] of Object.entries(raw as Record<string, unknown>)) {
    if (count >= MAX_CONTEXT_KEYS) break
    try {
      // A page may contribute a getter/thunk instead of a resolved value (e.g. a value that's
      // expensive or only meaningful at submit time) — resolved defensively, inside the same
      // try/catch as everything else here.
      const value = typeof rawValue === "function" ? (rawValue as () => unknown)() : rawValue
      if (value === null || value === undefined) continue
      const asString = typeof value === "string" ? value : JSON.stringify(value)
      if (typeof asString !== "string") continue
      out[key] = asString.slice(0, MAX_CONTEXT_STRING_LENGTH)
      count += 1
    } catch {
      // tier3: a context value that cannot be read or serialised is dropped; feedback still sends
      continue
    }
  }
  return out
}

/** EC3/EC6 idempotency key: minted once per dialog session, reused across a Retry so the
 * backend's ON CONFLICT DO NOTHING recognizes a resubmission of the same attempt. */
export function mintClientSubmissionId(): string {
  return crypto.randomUUID()
}
